import type { ChatType } from '@kanbu/schema';
import { Agent, AgentType, Role } from '@kanbu/schema/enums';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Badge, Dropdown, Input, Menu as UIMenu } from '@utima/ui';
import {
  CircleHelp,
  Building,
  ChevronsUpDown,
  PlusCircle,
  LibraryBig,
  BugPlayIcon,
  Blocks,
  Inbox,
  Edit,
  Package,
  Bot,
  Users,
} from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useInboxUnread } from '@/components/inbox/hooks/useInboxUnread';
import { AppSettings } from '@/constants/AppSettings';
import { useUser } from '@/hooks';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { isPartnerCapable } from '@/lib/routerUtils';
import { trpc } from '@/services/trpc';
import { useBoundStore } from '@/store/store';

import { ChatsSwitch } from './chats-switch';
import { MenuLink } from './MenuLink';
import { Protected } from '../protected/Protected';

export type MenuProps = {};

export const Menu = memo(function Menu({}: MenuProps) {
  const [organizationSearch, setOrganizationSearch] = useState('');
  const { data: chats } = trpc.chats.findAll.useQuery({
    pagination: {
      pageSize: 1000,
    },
  });
  const { data: organizations } = trpc.organizations.findAll.useQuery({
    pagination: {
      pageSize: 1000,
    },
  });
  const { setChat, chat: currentChat } = useBoundStore(
    useShallow(state => ({
      setChat: state.setChat,
      chat: state.chat,
    })),
  );

  const { user } = useUser();
  const isSuperAdmin = user?.role === Role.SuperAdmin;
  const partnerCapable = isPartnerCapable(user);
  const { unreadCount } = useInboxUnread(true);
  const handleOrganizationChange = useOrganizationChange();
  const { organization } = useCurrentOrganization();
  const currentOrganizationName = organization?.name;

  const filteredOrganizations = organizations?.items?.filter(org =>
    org.name.toLowerCase().includes(organizationSearch.toLowerCase()),
  );

  const rawChatItems = chats?.items as unknown;
  const chatItems = useMemo(
    () => (Array.isArray(rawChatItems) ? (rawChatItems as ChatType[]) : []),
    [rawChatItems],
  );

  const shouldShowOrganizationSearch = (organizations?.items?.length ?? 0) >= 6;

  useEffect(() => {
    if (chatItems.length === 0) {
      if (chats && currentChat) {
        setChat(null);
      }

      return;
    }

    // If there is no current chat, set the first chat as the current chat
    if (!currentChat) {
      setChat(chatItems[0]);
    }

    // If the current chat is not in the list of chats, set the first chat as the current chat
    if (currentChat && !chatItems.some(chat => chat.id === currentChat.id)) {
      setChat(chatItems[0]);
    }

    // Update existing chat, with new data when refetched
    if (currentChat) {
      const chat = chatItems.find(chat => chat.id === currentChat.id);

      if (chat) {
        setChat(chat);
      }
    }
  }, [chatItems, chats, currentChat, setChat]);

  const chatId = currentChat?.id;

  return (
    <UIMenu.Root>
      <UIMenu.Content>
        <UIMenu.Item>
          <MenuLink to='/inbox' Icon={Inbox}>
            <div className='flex w-full items-center justify-between'>
              <Trans>Inbox</Trans>
              {unreadCount > 0 && (
                <Badge
                  className='min-w-5 justify-center'
                  size='xs'
                  variant='primary'
                >
                  {unreadCount}
                </Badge>
              )}
            </div>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Item>
          <MenuLink to='/users' Icon={Users}>
            <div className='flex w-full items-center justify-between'>
              <Trans>Administrators</Trans>
            </div>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Item>
          <MenuLink to='/knowledge-base' Icon={LibraryBig}>
            <Trans>Knowledge Base</Trans>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Item>
          <MenuLink to='/agent-tools' Icon={Blocks}>
            <Trans>Agent Tools</Trans>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Label className='mt-6'>
          <Trans>Agent</Trans>
        </UIMenu.Label>
        <UIMenu.Item>
          <ChatsSwitch />
        </UIMenu.Item>

        {chatId && (
          <>
            <UIMenu.Item>
              <MenuLink
                to='/chats/$chatId/documents'
                params={{
                  chatId: chatId,
                }}
                Icon={LibraryBig}
              >
                <Trans>Knowledge Base</Trans>
              </MenuLink>
            </UIMenu.Item>
            {currentChat?.agent === Agent.ShopAssistant && (
              <UIMenu.Item>
                <MenuLink
                  to='/chats/$chatId/feeds'
                  params={{
                    chatId: chatId,
                  }}
                  Icon={Package}
                >
                  <Trans>Product feeds</Trans>
                </MenuLink>
              </UIMenu.Item>
            )}
            <UIMenu.Item>
              <MenuLink
                to='/chats/$chatId/edit'
                params={{
                  chatId: chatId,
                }}
                Icon={Edit}
              >
                <Trans>Configure</Trans>
              </MenuLink>
            </UIMenu.Item>
            <UIMenu.Item>
              <MenuLink
                to='/chats/$chatId/faqs'
                Icon={CircleHelp}
                params={{
                  chatId: chatId,
                }}
              >
                <Trans>FAQs</Trans>
              </MenuLink>
            </UIMenu.Item>
            {(currentChat?.agentType === AgentType.Chat ||
              currentChat?.agentType === AgentType.Voice) && (
              <UIMenu.Item>
                <UIMenu.Link asChild>
                  <a
                    target='_blank'
                    href={`${AppSettings.playgroundUrl}/${chatId}`}
                    className='!text-green-500'
                    rel='noreferrer'
                  >
                    <BugPlayIcon className='size-4 transition-none' />
                    <Trans>Playground</Trans>
                  </a>
                </UIMenu.Link>
              </UIMenu.Item>
            )}
          </>
        )}

        <UIMenu.Label className='mt-6'>
          <Trans>Organizations</Trans>
        </UIMenu.Label>
        <UIMenu.Item>
          <Dropdown.Root
            modal={false}
            onOpenChange={open => {
              if (!open) {
                setOrganizationSearch('');
              }
            }}
          >
            <Dropdown.Trigger className='m-1 flex w-full items-center justify-between rounded-radius border border-border px-3 py-2.5 text-sm font-medium transition-all hover:bg-accent'>
              {currentOrganizationName ?? <Trans>Choose organization</Trans>}
              <ChevronsUpDown className='size-4' />
            </Dropdown.Trigger>
            <Dropdown.Content
              side='bottom'
              align='end'
              className='max-h-60 min-w-52 overflow-y-auto'
            >
              {organizations?.items?.length && (
                <>
                  <Dropdown.Label>
                    <Trans>Organizations</Trans>
                  </Dropdown.Label>
                  {shouldShowOrganizationSearch && (
                    <div className='px-2 pb-2'>
                      <Input
                        data-cta='admin-menu-organization-search-input'
                        type='text'
                        placeholder={t`Search organizations...`}
                        value={organizationSearch}
                        onChange={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOrganizationSearch(e.target.value);
                        }}
                        onKeyDown={e => {
                          e.stopPropagation();
                        }}
                        autoFocus
                        className='h-8'
                      />
                    </div>
                  )}
                  <Dropdown.Separator />
                  <Dropdown.Group>
                    {filteredOrganizations &&
                    filteredOrganizations.length > 0 ? (
                      filteredOrganizations.map(organization => (
                        <Dropdown.Item
                          className='flex items-center justify-between'
                          key={organization.id}
                          onClick={() =>
                            handleOrganizationChange(organization.id)
                          }
                        >
                          {organization.name}
                        </Dropdown.Item>
                      ))
                    ) : (
                      <div className='px-2 py-3 text-center text-sm text-muted-fg'>
                        <Trans>No organizations found</Trans>
                      </div>
                    )}
                  </Dropdown.Group>
                  <Dropdown.Separator />
                </>
              )}
              <Dropdown.Label>
                <Trans>Management</Trans>
              </Dropdown.Label>
              <Dropdown.Group>
                <Dropdown.Item asChild>
                  <Link
                    data-cta='admin-menu-organizations-add-new-link'
                    to='/organizations/create'
                  >
                    <PlusCircle className='mr-2 size-4' />
                    <Trans>Add new</Trans>
                  </Link>
                </Dropdown.Item>
              </Dropdown.Group>
            </Dropdown.Content>
          </Dropdown.Root>
        </UIMenu.Item>

        <Protected roles={[Role.SuperAdmin]}>
          <UIMenu.Label className='mt-6 text-amber-600'>
            <Trans>Superadmin</Trans>
          </UIMenu.Label>
          <UIMenu.Item>
            <MenuLink
              to='/organizations'
              Icon={Building}
              activeProps={{ className: 'bg-amber-500/30' }}
            >
              <Trans>Organizations</Trans>
            </MenuLink>
          </UIMenu.Item>
          <UIMenu.Item>
            <MenuLink
              to='/chats'
              Icon={Bot}
              activeProps={{ className: 'bg-amber-500/30' }}
            >
              <Trans>Chats</Trans>
            </MenuLink>
          </UIMenu.Item>
        </Protected>

        {!isSuperAdmin && partnerCapable && (
          <>
            <UIMenu.Label className='mt-6'>
              <Trans>Partner</Trans>
            </UIMenu.Label>
            <UIMenu.Item>
              <MenuLink to='/organizations' Icon={Building}>
                <Trans>Organizations</Trans>
              </MenuLink>
            </UIMenu.Item>
            <UIMenu.Item>
              <MenuLink to='/chats' Icon={Bot}>
                <Trans>Chats</Trans>
              </MenuLink>
            </UIMenu.Item>
          </>
        )}
      </UIMenu.Content>
    </UIMenu.Root>
  );
});
